import Vue from 'vue'
import VueNativeSock from 'vue-native-websocket'
import store from '../store'

const vm = new Vue()
let isConnecting = false // Lock to prevent multiple simultaneous connections
let connectionPromise = null // Store ongoing connection promise
let lastSentPayload = null // Track the last payload actually sent via WebSocket (not just stored in state)

// Note: Vue.use will be called AFTER mutations are defined (at end of file)
// Note: PING/PONG is handled automatically by the server and browser

const connectWS = (url) => {
  // Check if already connected
  if (vm.$socket && vm.$socket.readyState === 1) {
    return connectionPromise || Promise.resolve()
  }

  // If connection is in progress, return the existing promise
  if (isConnecting && connectionPromise) {
    return connectionPromise
  }

  isConnecting = true

  // Connect using vue-native-websocket
  vm.$connect(url)

  // Return a promise that resolves when connected or rejects on error
  connectionPromise = new Promise((resolve, reject) => {
    let checkInterval = null
    let timeoutHandle = null
    let isHandlersSetup = false

    const cleanup = () => {
      if (checkInterval) clearInterval(checkInterval)
      if (timeoutHandle) clearTimeout(timeoutHandle)
      isConnecting = false
    }

    // Check for successful connection
    checkInterval = setInterval(() => {
      if (vm.$socket && vm.$socket.readyState === 1 && !isHandlersSetup) {
        isHandlersSetup = true
        cleanup()

        // NOTE: With connectManually: true, vue-native-websocket doesn't automatically
        // attach event handlers. We need to set them up manually, but we let
        // vue-native-websocket handle the actual mutation calls through its store integration.
        // This is NOT duplicating - vue-native-websocket's internal handlers are only
        // active when using automatic connection. With manual connection, we bridge to them.

        // Set up message handler - parse and dispatch to store
        vm.$socket.onmessage = (event) => {
          let message = event.data

          // Parse JSON if needed
          if (typeof message === 'string') {
            try {
              message = JSON.parse(message)
            } catch (e) {
              console.error('Failed to parse WebSocket message:', e)
              return
            }
          }

          // Trigger the mutation via store
          store.commit('ws/SOCKET_ONMESSAGE', message)
        }

        // Set up close handler with cleanup
        vm.$socket.onclose = (event) => {
          cleanup()
          connectionPromise = null
          store.commit('ws/SOCKET_ONCLOSE', event)
        }

        // Set up error handler with cleanup
        vm.$socket.onerror = (error) => {
          cleanup()
          connectionPromise = null
          console.error('WebSocket error:', error)
          store.commit('ws/SOCKET_ONERROR', error)
        }

        // Manually trigger SOCKET_ONOPEN since we're connecting manually
        store.commit('ws/SOCKET_ONOPEN', { currentTarget: vm.$socket })

        resolve()
      }
    }, 100)

    // Set timeout to prevent hanging
    timeoutHandle = setTimeout(() => {
      cleanup()
      connectionPromise = null
      reject(new Error('Connection timeout after 10 seconds'))
    }, 10000)
  })

  return connectionPromise
}

const disconnectWS = () => {
  vm.$disconnect()
  isConnecting = false // Reset connection lock
  connectionPromise = null // Clear connection promise
  lastSentPayload = null // Reset so payload will be sent again on reconnect
}

export const state = {
  socket: {
    isConnected: false,
    isConnecting: false, // Track if connection is in progress
    message: '',
    reconnectError: false,
    connectionAttempts: 0,
  },
  messageHandlers: [],
  payload: null, // Store the latest payload
  isInitialized: false,
  alertRules: {
    urgentAlertRules: [],
    importantAlertRules: [],
    autoPopupAlertRules: [],
  },
}

export const mutations = {
  SOCKET_ONOPEN(state, event) {
    Vue.prototype.$socket = event.currentTarget
    state.socket.isConnected = true
    state.socket.isConnecting = false
    state.socket.connectionAttempts = 0

    // Note: We do NOT send PING messages from the client
    // The server sends PING, and the browser automatically responds with PONG
    // This is handled by the WebSocket protocol at the browser level
  },

  SOCKET_ONCLOSE(state) {
    state.socket.isConnected = false
    state.socket.isConnecting = false

    // Reset connection lock and promise
    isConnecting = false
    connectionPromise = null
    lastSentPayload = null // Reset so payload will be sent again on reconnect
  },

  SOCKET_ONERROR(state, event) {
    console.error('WebSocket error:', event)
  },

  SOCKET_ONMESSAGE(state, message) {
    state.socket.message = message

    // Parse message if it's a string
    const parsedMessage = typeof message === 'string' ? JSON.parse(message) : message

    // Distribute to all registered handlers
    state.messageHandlers.forEach((handler, index) => {
      try {
        handler(parsedMessage)
      } catch (error) {
        console.error(`Handler ${index + 1} error:`, error)
      }
    })
  },

  SOCKET_RECONNECT(state, count) {
    state.socket.connectionAttempts = count
  },

  SOCKET_RECONNECT_ERROR(state) {
    state.socket.reconnectError = true
  },

  REGISTER_MESSAGE_HANDLER(state, handler) {
    if (!state.messageHandlers.includes(handler)) {
      state.messageHandlers.push(handler)
    }
  },

  UNREGISTER_MESSAGE_HANDLER(state, handler) {
    const index = state.messageHandlers.indexOf(handler)
    if (index > -1) {
      state.messageHandlers.splice(index, 1)
    }
  },

  SET_PAYLOAD(state, payload) {
    state.payload = payload
  },

  SET_INITIALIZED(state, value) {
    state.isInitialized = value
  },

  SET_ALERT_RULES(state, alertRules) {
    state.alertRules = {
      urgentAlertRules: alertRules.urgentAlertRules || [],
      importantAlertRules: alertRules.importantAlertRules || [],
      autoPopupAlertRules: alertRules.autoPopupAlertRules || [],
    }
  },

  SET_CONNECTING(state, value) {
    state.socket.isConnecting = value
  },
}

export const actions = {
  /**
   * Initialize WebSocket connection with payload
   * Should be called once when app starts
   */
  async initialize({ commit, state, dispatch }, { url, payload }) {
    // Check if already initialized and connected
    if (state.isInitialized && state.socket.isConnected) {
      return
    }

    try {
      commit('SET_CONNECTING', true)

      // connectWS now returns a promise that handles connection logic
      // and waits for ongoing connections automatically
      await connectWS(url)

      commit('SET_CONNECTING', false)

      // Send payload if provided
      if (payload) {
        await dispatch('updateTags', payload)
      }

      commit('SET_INITIALIZED', true)
    } catch (error) {
      console.error('WebSocket initialization failed:', error)
      commit('SET_CONNECTING', false)
      throw error
    }
  },

  /**
   * Connect WebSocket (alias for compatibility)
   */
  async WSConnect({ commit, state }, url) {
    // Check if already connected
    if (state.socket.isConnected) {
      return
    }

    try {
      commit('SET_CONNECTING', true)

      // connectWS now returns a promise that handles connection logic
      // and waits for ongoing connections automatically
      await connectWS(url)

      commit('SET_CONNECTING', false)
      commit('SET_INITIALIZED', true)
    } catch (error) {
      console.error('WebSocket connection failed:', error)
      commit('SET_CONNECTING', false)
      throw error
    }
  },

  /**
   * Update subscription tags (payload)
   */
  updateTags({ state, commit }, payload) {
    if (!state.socket.isConnected) {
      return false
    }

    // Prevent duplicate update_tags: only send if payload has changed from what was ACTUALLY SENT
    // Note: We check lastSentPayload (what was sent via WebSocket), not state.payload (what's in Vuex)
    // because state.payload might be set from cache before the WebSocket message is sent
    if (lastSentPayload === payload) {
      return true // Return true since we're already subscribed with this payload
    }

    if (vm.$socket && vm.$socket.readyState === 1) {
      const message = {
        type: 'update_tags',
        data: payload,
      }

      // Use sendObj if available, otherwise use send with JSON.stringify
      if (typeof vm.$socket.sendObj === 'function') {
        vm.$socket.sendObj(message)
      } else {
        vm.$socket.send(JSON.stringify(message))
      }

      // Track that we actually sent this payload via WebSocket
      lastSentPayload = payload
      commit('SET_PAYLOAD', payload)
      return true
    }

    return false
  },

  /**
   * Reconnect WebSocket
   */
  async reconnect({ state, dispatch }) {
    if (state.socket.isConnected) {
      return
    }

    const url = `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
    const payload = state.payload || localStorage.getItem('payload')

    try {
      await dispatch('initialize', { url, payload })
    } catch (error) {
      console.error('Reconnection failed:', error)
      throw error
    }
  },

  /**
   * Register message handler
   */
  registerMessageHandler({ commit }, handler) {
    commit('REGISTER_MESSAGE_HANDLER', handler)
  },

  /**
   * Unregister message handler
   */
  unregisterMessageHandler({ commit }, handler) {
    commit('UNREGISTER_MESSAGE_HANDLER', handler)
  },

  /**
   * Clean disconnect
   */
  disconnect({ commit, state }) {
    disconnectWS()
    commit('SET_INITIALIZED', false)

    // Clear all handlers
    state.messageHandlers.forEach((handler) => {
      commit('UNREGISTER_MESSAGE_HANDLER', handler)
    })
  },

  /**
   * Disconnect WebSocket (alias for compatibility)
   */
  WSDisconnect({ commit, dispatch }) {
    dispatch('disconnect')
  },
}

export const getters = {
  isConnected: (state) => state.socket.isConnected,
  isConnecting: (state) => state.socket.isConnecting,
  isInitialized: (state) => state.isInitialized,
  messageHandlers: (state) => state.messageHandlers,
  payload: (state) => state.payload,
  urgentAlertRules: (state) => state.alertRules.urgentAlertRules,
  importantAlertRules: (state) => state.alertRules.importantAlertRules,
  autoPopupAlertRules: (state) => state.alertRules.autoPopupAlertRules,
}

// Initialize vue-native-websocket with store integration
// IMPORTANT: This is called at the END of the file so mutations are defined
Vue.use(VueNativeSock, `${process.env.VUE_APP_WS_URL}/ws`, {
  store: store,
  mutations: {
    SOCKET_ONOPEN: 'ws/SOCKET_ONOPEN',
    SOCKET_ONCLOSE: 'ws/SOCKET_ONCLOSE',
    SOCKET_ONERROR: 'ws/SOCKET_ONERROR',
    SOCKET_ONMESSAGE: 'ws/SOCKET_ONMESSAGE',
    SOCKET_RECONNECT: 'ws/SOCKET_RECONNECT',
    SOCKET_RECONNECT_ERROR: 'ws/SOCKET_RECONNECT_ERROR',
  },
  format: 'json',
  connectManually: true,
  reconnection: false, // DISABLE automatic reconnection to prevent multiple connections
  reconnectionAttempts: 0,
  reconnectionDelay: 0,
})
